import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, LogOut, User as UserIcon, Trash2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api_getMyProfile, api_upsertMyProfile, api_deleteAccount } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const FRUSTRATIONS = [
  "Miss too many greens",
  "3-putts",
  "Inconsistent contact",
  "Bad wedge distance control",
  "Tee shots off-line",
  "Poor short game around greens",
];
const GOALS = [
  "Break 100",
  "Break 90",
  "Break 80",
  "Break 70",
  "Improve short game",
  "Hit more greens in regulation",
  "Play more consistent rounds",
  "Hit more fairways off the tee",
];
const TIMES = [15, 30, 45, 60, 90, 120];
const LOCATIONS = [
  { id: "range", label: "Driving range" },
  { id: "short_game", label: "Short game area" },
  { id: "putting", label: "Putting green" },
  { id: "home", label: "Home / net" },
];

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await api_getMyProfile();
        setProfile(p);
      } catch {
        toast.error("Could not load your profile.");
      }
    })();
  }, []);

  if (!profile) {
    return <AppShell profile={null}><div className="text-center text-slate-500 py-20">Loading…</div></AppShell>;
  }

  const toggleArr = (key, val) =>
    setProfile((p) => ({ ...p, [key]: p[key].includes(val) ? p[key].filter((x) => x !== val) : [...p[key], val] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: profile.name,
        handicap: profile.handicap,
        frustrations: profile.frustrations,
        goals: profile.goals,
        default_time_minutes: profile.default_time_minutes,
        locations: Array.isArray(profile.locations) && profile.locations.length ? profile.locations : ["range"],
      };
      const updated = await api_upsertMyProfile(payload);
      setProfile(updated);
      toast.success("Saved.");
    } catch {
      toast.error("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const onSignOut = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const onDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api_deleteAccount();
      setUser(null);
      toast.success("Account deleted.");
      navigate("/", { replace: true });
    } catch {
      toast.error("Could not delete account. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <AppShell profile={profile}>
      <button onClick={() => navigate("/home")} data-testid="settings-back" className="text-sm font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Settings</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Your profile</h1>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
          {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <UserIcon className="w-6 h-6 text-slate-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-lg truncate" data-testid="settings-account-name">{user?.name}</p>
          <p className="text-sm text-slate-500 truncate" data-testid="settings-account-email">{user?.email}</p>
        </div>
        <button onClick={onSignOut} data-testid="settings-signout" className="btn-ghost text-sm py-2 px-4">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="mt-6 grid gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Display name</p>
          <input
            data-testid="settings-name"
            value={profile.name}
            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base font-semibold focus:border-[#0B2545] focus:ring-2 focus:ring-[#0B2545]/20 outline-none"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Handicap</p>
          <div className="mt-3 flex items-end gap-6">
            <div className="text-5xl font-heading font-black tabular-nums" data-testid="settings-handicap-display">
              {profile.handicap < 0 ? `+${Math.abs(profile.handicap)}` : profile.handicap}
            </div>
            <span className="text-slate-500 font-semibold mb-2">handicap</span>
          </div>
          <input
            data-testid="settings-handicap"
            type="range"
            min={-2}
            max={30}
            value={profile.handicap}
            onChange={(e) => setProfile({ ...profile, handicap: parseInt(e.target.value, 10) })}
            className="mt-4 w-full accent-[#0B2545]"
          />
          <div className="flex justify-between text-xs font-bold text-slate-400 mt-1">
            <span>low (+2 to 5)</span><span>mid (10–19)</span><span>high (20–30)</span>
          </div>
          <p className="mt-3 text-xs text-slate-500 font-semibold">Practice identity: {profile.identity}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Frustrations</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {FRUSTRATIONS.map((f) => {
              const active = profile.frustrations.includes(f);
              return (
                <button key={f} data-testid={`settings-frustration-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => toggleArr("frustrations", f)}
                  className={`text-left p-3 rounded-xl border-2 text-sm font-bold transition-all ${active ? "border-[#0B2545] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                  {f}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Goals</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {GOALS.map((g) => {
              const active = profile.goals.includes(g);
              return (
                <button key={g} data-testid={`settings-goal-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => toggleArr("goals", g)}
                  className={`text-left p-3 rounded-xl border-2 text-sm font-bold transition-all ${active ? "border-[#0B2545] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                  {g}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Default session length</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {TIMES.map((t) => {
              const active = profile.default_time_minutes === t;
              return (
                <button key={t} data-testid={`settings-time-${t}`} onClick={() => setProfile({ ...profile, default_time_minutes: t })}
                  className={`px-4 py-2 rounded-full font-bold text-sm transition-all ${active ? "bg-[#0B2545] text-white" : "bg-slate-100 hover:bg-slate-200"}`}>
                  {t} min
                </button>
              );
            })}
          </div>
          <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Preferred locations <span className="font-semibold normal-case tracking-normal text-slate-400">(pick one or more)</span></p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {LOCATIONS.map((l) => {
              const locs = Array.isArray(profile.locations) ? profile.locations : [];
              const active = locs.includes(l.id);
              return (
                <button key={l.id} data-testid={`settings-location-${l.id}`} onClick={() => {
                  const cur = locs.slice();
                  const next = active ? cur.filter((x) => x !== l.id) : [...cur, l.id];
                  setProfile({ ...profile, locations: next.length ? next : cur });
                }}
                  className={`p-3 rounded-xl border-2 text-left font-bold text-sm transition-all ${active ? "border-[#0B2545] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="settings-save" className="btn-primary disabled:opacity-60">
          {saving ? "Saving…" : "Save changes"} <Save className="w-4 h-4" />
        </button>
      </div>

      {/* Danger zone */}
      <div className="mt-12 border-t border-slate-200 pt-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-600">Danger zone</p>
        <div className="mt-4 bg-white border-2 border-red-200 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="font-heading font-bold text-lg">Delete my account</p>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              Permanently delete your profile, every session, and your streak history. This can't be undone.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="settings-delete-account" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-red-600 text-white font-bold text-sm hover:bg-red-700 active:scale-[0.98] transition-all shrink-0">
                <Trash2 className="w-4 h-4" /> Delete account
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your Fairway Lab account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove your profile, all practice sessions, your streak, and any reflections.
                  You can sign in with Google again later, but your history will be gone for good.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="delete-confirm"
                  disabled={deleting}
                  onClick={onDeleteAccount}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? "Deleting…" : "Yes, delete forever"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </AppShell>
  );
}
